// [claude] Semantic design tokens for the "Ink" design system (2026-07 modernization).
// Single source of truth for both color modes. Dark ("ink") is the default; light
// values are lifted from the mobile app's lightTheme (tesla-mobile/app/colors.js) so
// web and app read as one product. Delivered via styled-components ThemeProvider in
// ScreenWrapper. Key names are a superset of shared/brandingTheme.js so the SSO/OAuth
// surfaces can converge on this module (Phase 4 of tasks/todo.md).
// Rules: no pure #000 bases, no pure #FFF text, no glow/grain/glass decoration.
// Gold is reserved for money moments (pricing, Pro, CTAs on money pages).

const fonts = {
	sans: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
	mono: "'Geist Mono', 'SF Mono', Monaco, 'Cascadia Code', monospace",
}

const shared = {
	radius: '10px',
	radiusLg: '14px',
	radiusFull: '999px',
	fontFamily: fonts.sans,
	fontMono: fonts.mono,
	motion: {
		fast: '0.15s',
		base: '0.2s',
		slow: '0.3s',
	},
}

export const darkTheme = {
	...shared,
	name: 'dark',
	// surfaces
	base: '#0A0C10',
	pageBg: '#0A0C10',
	bg: '#0A0C10',
	surface: '#0F131A',
	surfaceRaised: '#1A202C',
	border: 'rgba(255, 255, 255, 0.07)',
	borderStrong: 'rgba(255, 255, 255, 0.14)',
	borderFocus: 'rgba(16, 171, 255, 0.5)',
	ring: 'rgba(16, 171, 255, 0.12)',
	// text
	text: '#F5F6F7',
	textSecondary: '#9BA1A8',
	textTertiary: '#787E88',
	// accents
	accent: '#10ABFF',
	accentText: '#FFFFFF',
	accentDim: 'rgba(16, 171, 255, 0.12)',
	gold: '#FCCE0D',
	goldStrong: '#E5BC2A',
	goldText: '#1C2028',
	success: '#8BC16A',
	error: '#F67171',
	alert: '#FF9839',
	// forms
	inputBg: 'rgba(255, 255, 255, 0.05)',
	inputBorder: 'rgba(255, 255, 255, 0.10)',
	disabledBg: 'rgba(255, 255, 255, 0.05)',
}

export const lightTheme = {
	...shared,
	name: 'light',
	// surfaces (mobile lightTheme: base #F2F7F9, card #FFFFFF)
	base: '#F2F7F9',
	pageBg: '#F2F7F9',
	bg: '#F2F7F9',
	surface: '#FFFFFF',
	surfaceRaised: '#FFFFFF',
	border: 'rgba(16, 24, 40, 0.10)',
	borderStrong: 'rgba(16, 24, 40, 0.18)',
	borderFocus: 'rgba(13, 142, 232, 0.55)',
	ring: 'rgba(13, 142, 232, 0.14)',
	// text (mobile: highlight #262A42, subtle #788195, medium #657185)
	text: '#262A42',
	textSecondary: '#657185',
	textTertiary: '#788195',
	// accents (mobile light: blue #0D8EE8, proGold #E0AF22)
	accent: '#0D8EE8',
	accentText: '#FFFFFF',
	accentDim: 'rgba(13, 142, 232, 0.10)',
	gold: '#E0AF22',
	goldStrong: '#C79A15',
	goldText: '#211C01',
	success: '#76B05A',
	error: '#EB5353',
	alert: '#F48530',
	// forms
	inputBg: '#FFFFFF',
	inputBorder: 'rgba(16, 24, 40, 0.14)',
	disabledBg: 'rgba(16, 24, 40, 0.04)',
}

const THEME_STORAGE_KEY = 'tezlab-web-theme'

// Light mode is PARKED until the full redesign (Ben, 2026-07-03): the palette,
// variable bridge, and setThemeName machinery below all still work, but nothing
// resolves to light - no query param, no stored preference, no UI.
export function resolveThemeName() {
	return 'dark'
}

export function getTheme(name = resolveThemeName()) {
	return name === 'light' ? lightTheme : darkTheme
}

export const THEME_EVENT = 'tezlab:theme-change'

// The legacy styles.colors palette is bridged to these CSS variables, so setting
// them here re-skins every styled-component without per-component theme wiring.
function cssVarsFor(theme) {
	const light = theme.name === 'light'
	return {
		'--tz-base': theme.base,
		'--tz-surface': theme.surface,
		'--tz-surface-raised': theme.surfaceRaised,
		'--tz-wash': light ? 'rgba(16, 24, 40, 0.04)' : 'rgba(255, 255, 255, 0.04)',
		'--tz-hairline': light ? 'rgba(16, 24, 40, 0.10)' : 'rgba(255, 255, 255, 0.08)',
		'--tz-hairline-strong': light ? 'rgba(16, 24, 40, 0.18)' : 'rgba(255, 255, 255, 0.12)',
		'--tz-gold-border': light ? 'rgba(199, 154, 21, 0.55)' : 'rgba(252, 206, 13, 0.45)',
		'--tz-text-strong': light ? '#1A1E25' : '#FFFFFF',
		'--tz-text': theme.text,
		'--tz-text-secondary': theme.textSecondary,
		'--tz-text-tertiary': theme.textTertiary,
		'--tz-accent': theme.accent,
		'--tz-gold': theme.gold,
		'--tz-success': theme.success,
		'--tz-error': theme.error,
		'--tz-alert': theme.alert,
	}
}

// Keeps the document chrome (scrollbars, form controls, tap highlights, PWA bar)
// and the CSS-variable bridge in sync with the active theme.
export function applyThemeToDocument(theme) {
	try {
		document.body.style.backgroundColor = theme.base
		document.documentElement.style.colorScheme = theme.name
		const vars = cssVarsFor(theme)
		Object.keys(vars).forEach(k => document.documentElement.style.setProperty(k, vars[k]))
		let meta = document.querySelector('meta[name="theme-color"]')
		if (!meta) {
			meta = document.createElement('meta')
			meta.setAttribute('name', 'theme-color')
			document.head.appendChild(meta)
		}
		meta.setAttribute('content', theme.base)
	} catch (e) {
		// no-op outside the browser
	}
}

// Runtime switch: persists, re-applies the document bridge, and notifies
// mounted ScreenWrappers so the styled-components ThemeProvider updates too.
export function setThemeName(name) {
	try {
		window.localStorage.setItem(THEME_STORAGE_KEY, name)
	} catch (e) {
		// private mode: session-only switch
	}
	applyThemeToDocument(getTheme(name))
	try {
		window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { name } }))
	} catch (e) {
		// no-op outside the browser
	}
}
